import { useState, useRef, useEffect } from 'react';
import { Languages, Check, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGS, normalizeLang } from '../i18n/index.js';

// 介面語系切換器。
//   compact=true → 收起側欄（只顯示 icon）
//   variant='header' → 右上 header 緊湊膠囊樣式，下拉往下開、靠右對齊
//   variant='sidebar'（預設）→ 側欄底部全寬，下拉往上開
export default function LanguageSwitcher({ compact = false, variant = 'sidebar' }) {
    const { i18n, t } = useTranslation('common');
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const current = normalizeLang(i18n.language);
    const currentMeta = SUPPORTED_LANGS.find((l) => l.code === current) || SUPPORTED_LANGS[0];
    const isHeader = variant === 'header';

    useEffect(() => {
        if (!open) return;
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [open]);

    const choose = (code) => {
        i18n.changeLanguage(code);
        setOpen(false);
    };

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen((o) => !o)}
                title={t('language.switch', '切換語言')}
                className={[
                    'flex items-center rounded-full border border-cream-200 bg-white/70 hover:bg-white hover:border-accent-300 transition text-ink-700',
                    isHeader ? 'gap-1.5 pl-2.5 pr-2 py-1.5' : (compact ? 'justify-center w-full py-2 rounded-md' : 'gap-2 px-3 py-2 w-full rounded-md'),
                ].join(' ')}
            >
                <Languages size={isHeader ? 15 : 16} strokeWidth={1.7} className="shrink-0 text-accent-500" />
                {isHeader ? (
                    <>
                        <span className="text-sm leading-none">{currentMeta.flag}</span>
                        <ChevronDown size={13} className="text-ink-400" />
                    </>
                ) : (!compact && (
                    <span className="text-sm font-medium truncate">
                        {currentMeta.flag} {currentMeta.label}
                    </span>
                ))}
            </button>
            {open && (
                <ul
                    className={[
                        'absolute bg-white border border-cream-200 rounded-lg shadow-lg z-40 py-1',
                        isHeader ? 'top-full right-0 mt-1.5 w-44' : (compact ? 'bottom-full mb-1 left-0 w-40' : 'bottom-full mb-1 left-0 right-0'),
                    ].join(' ')}
                >
                    {SUPPORTED_LANGS.map((l) => (
                        <li key={l.code}>
                            <button
                                onClick={() => choose(l.code)}
                                className={[
                                    'w-full text-left px-3 py-2 text-sm hover:bg-cream-100 transition flex items-center justify-between gap-2',
                                    l.code === current ? 'bg-cream-100 font-medium text-accent-700' : 'text-ink-700',
                                ].join(' ')}
                            >
                                <span className="truncate">{l.flag} {l.label}</span>
                                {l.code === current && <Check size={14} className="text-accent-500 shrink-0" />}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
